import { Alert, Form, FormInstance, Spin } from "antd";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IntegrationFormData, IntegrationProtocol } from "../../../api";
import { useIntegrationStateContext } from "../context/IntegrationStateContext";

interface FormWrapperProps {
  children: React.ReactNode;
  form: FormInstance<IntegrationFormData>;
  loading: boolean;
  setFormDataReady: (value: boolean) => void;
  setProtocolChanged: (newProtocol: IntegrationProtocol) => void;
}

export const FormWrapper = ({
  children,
  form,
  loading,
  setFormDataReady,
  setProtocolChanged,
}: FormWrapperProps) => {
  const { t } = useTranslation();
  const isMountedRef = useRef(true);
  const { updateIntegrationState, setUpdateIntegrationState } =
    useIntegrationStateContext();

  useEffect(() => {
    isMountedRef.current = true;

    const checkFormValidity = async () => {
      const ifFormValid = await validateFormDataReadyForSave(form);
      setUpdateIntegrationState({
        ...updateIntegrationState,
        disabled: !ifFormValid,
      });
      if (isMountedRef.current) {
        setFormDataReady(ifFormValid);
      }
    };

    checkFormValidity();

    return () => {
      isMountedRef.current = false;
    };
  }, [
    form.getFieldsValue().endpointUrl,
    form.getFieldsValue().integrationName,
    form.getFieldsValue().authValue,
    form.getFieldsValue().templateContent,
    form.getFieldsValue().templateContentType,
    setFormDataReady,
  ]);

  const handleValuesChange = async (e: IntegrationFormData) => {
    const ifFormValid = await validateFormDataReadyForSave(form);

    if (isMountedRef.current) {
      setFormDataReady(ifFormValid);

      if (e.integrationProtocol) {
        setProtocolChanged(e.integrationProtocol as IntegrationProtocol);
      }
    }
  };

  return (
    <Spin
      size="large"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
      spinning={loading}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        style={{ padding: 16, minWidth: 680 }}
        onValuesChange={handleValuesChange}
        data-testid="integration-form-wrapper"
      >
        {updateIntegrationState.error && (
          <Alert
            message={t("error-save")}
            type="error"
            showIcon
            closable
            onClose={() =>
              setUpdateIntegrationState({
                ...updateIntegrationState,
                error: false,
              })
            }
            style={{ marginBottom: 16 }}
          />
        )}
        {children}
      </Form>
    </Spin>
  );
};

const validateFormDataReadyForSave = async (
  form: FormInstance<IntegrationFormData>
) => {
  try {
    await form.validateFields();
  } catch (error) {}
  const errors = form.getFieldsError();

  const isFormValid = errors.every(
    (fieldError) => fieldError.errors.length === 0
  );
  return isFormValid;
};
